// Normalization map for all Arabic area slugs (hyphens, spaces, hamzas, taa-marbuta)
// Ensures zero 404s when navigating or resolving area pages.

import { areas } from './areas';

export function normalizeArabicText(text = '') {
  return decodeURIComponent(String(text || ''))
    .trim()
    .replace(/[أإآ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/[\u0660-\u0669]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48))
    .replace(/[-_\s]+/g, ' ')
    .trim();
}

export function toArabicSlug(name = '') {
  return encodeURIComponent(String(name || '').trim().replace(/\s+/g, '-'));
}

// Precomputed lookup map from normalized form to canonical area name
const slugMap = new Map();

// First add manual aliases so explicit overrides take priority
const manualAliases = {
  // New Cairo & 5th Settlement
  'التجمع': 'التجمع الخامس',
  'التجمع-الخامس': 'التجمع الخامس',
  'التجمع الخامس': 'التجمع الخامس',
  'التجمع الاول': 'التجمع الأول',
  'التجمع-الاول': 'التجمع الأول',
  'التجمع-الأول': 'التجمع الأول',
  'التجمع الأول': 'التجمع الأول',
  'التجمع التالت': 'التجمع الثالث',
  'التجمع-التالت': 'التجمع الثالث',
  'التجمع-الثالث': 'التجمع الثالث',
  'التجمع الثالث': 'التجمع الثالث',
  'التسعين': 'التجمع الخامس',
  'شارع التسعين': 'التجمع الخامس',
  'شارع-التسعين': 'التجمع الخامس',
  'التسعين-الجنوبي': 'التجمع الخامس',
  'التسعين الجنوبي': 'التجمع الخامس',
  'التسعين-الشمالي': 'التجمع الخامس',
  'التسعين الشمالي': 'التجمع الخامس',

  // Nasr City & Sub-neighborhoods
  'مدينه نصر': 'مدينة نصر',
  'مدينه-نصر': 'مدينة نصر',
  'مدينة-نصر': 'مدينة نصر',
  'مدينة نصر': 'مدينة نصر',
  'مكرم عبيد': 'مدينة نصر',
  'مكرم-عبيد': 'مدينة نصر',
  'عباس العقاد': 'مدينة نصر',
  'عباس-العقاد': 'مدينة نصر',
  'مصطفى النحاس': 'مدينة نصر',
  'مصطفي النحاس': 'مدينة نصر',
  'شارع مصطفي النحاس': 'مدينة نصر',
  'شارع النصر': 'مدينة نصر',
  'الحي السابع': 'مدينة نصر',
  'الحي السادس': 'مدينة نصر',
  'الحي العاشر': 'مدينة نصر',
  'الحي الثامن': 'مدينة نصر',
  'زهراء مدينة نصر': 'مدينة نصر',
  'سيتي ستارز': 'مدينة نصر',

  // Heliopolis & Sub-neighborhoods
  'مصر الجديده': 'مصر الجديدة',
  'مصر-الجديده': 'مصر الجديدة',
  'مصر-الجديدة': 'مصر الجديدة',
  'مصر الجديدة': 'مصر الجديدة',
  'هليوبوليس': 'مصر الجديدة',
  'روكسي': 'مصر الجديدة',
  'الميرغني': 'مصر الجديدة',
  'تريومف': 'مصر الجديدة',
  'شيراتون': 'مصر الجديدة',
  'النزهة': 'مصر الجديدة',
  'النزهه': 'مصر الجديدة',
  'شارع النزهه': 'مصر الجديدة',
  'النزهة الجديدة': 'مصر الجديدة',
  'شارع الحجاز': 'مصر الجديدة',
  'ألماظة': 'مصر الجديدة',
  'الماظه': 'مصر الجديدة',
  'شارع الثورة': 'مصر الجديدة',
  'سانت فاتيما': 'مصر الجديدة',
  'كلية البنات': 'مصر الجديدة',
  'الكوربة': 'مصر الجديدة',

  // East Cairo & Others
  'القطاميه': 'القطامية',
  'القطامية': 'القطامية',
  'الحرفيين': 'الحرفيين',
  'مدينة السلام': 'مدينة السلام',
  'مدينه السلام': 'مدينة السلام',
  'السلام': 'مدينة السلام',
  'جسر السويس': 'جسر السويس',
  'شارع جسر السويس': 'جسر السويس',
  'ألف مسكن': 'جسر السويس',
  'الالف مسكن': 'جسر السويس',

  // South Cairo & Nile
  'المعادي': 'المعادي',
  'المعادي الجديدة': 'المعادي',
  'دجلة المعادي': 'المعادي',
  'شارع 9': 'المعادي',
  'كارفور المعادي': 'المعادي',
  'زهراء المعادي': 'زهراء المعادي',
  'صقر قريش': 'صقر قريش',
  'المقطم': 'المقطم',
  'شارع 9 بالمقطم': 'المقطم',
  'الهضبة الوسطى': 'المقطم',
  'الهضبه الوسطى': 'المقطم',
  'الهضبه الوسطى بالمقطم': 'المقطم',
  'حلوان': 'حلوان',
  'حلوان البلد': 'حلوان',
  'عين حلوان': 'حلوان',
  'المعصرة': 'حلوان',
  'المعصره': 'حلوان',
  'طرة': 'حلوان',
  'طره البلد': 'حلوان',
  '15 مايو': 'حلوان',
  'التبين': 'حلوان',
  'المنيل': 'المنيل',
  'منيل الروضة': 'المنيل',
  'منيل-الروضة': 'المنيل',
  'جزيرة الروضة': 'المنيل',

  // Giza Core
  'الجيزه': 'الجيزة',
  'الجيزة': 'الجيزة',
  'ميدان الجيزة': 'الجيزة',
  'شارع مراد': 'الجيزة',
  'البحر الأعظم': 'الجيزة',
  'المهندسين': 'المهندسين',
  'جامعة الدول': 'المهندسين',
  'جامعة الدول العربية': 'المهندسين',
  'شارع شهاب': 'المهندسين',
  'شارع سوريا': 'المهندسين',
  'شارع وادي النيل': 'المهندسين',
  'ميدان سفنكس': 'المهندسين',
  'ميدان لبنان': 'المهندسين',
  'الدقي': 'الدقي',
  'ميدان الدقي': 'الدقي',
  'مصدق': 'الدقي',
  'شارع التحرير': 'الدقي',
  'شارع السودان': 'الدقي',
  'العجوزه': 'العجوزة',
  'العجوزة': 'العجوزة',
  'ميدان الكيت كات': 'العجوزة',
  'الكيت كات': 'العجوزة',
  'امبابه': 'إمبابة',
  'إمبابة': 'إمبابة',
  'إمبابه': 'إمبابة',
  'الوراق': 'الوراق',
  'المنيب': 'المنيب',
  'ميدان المنيب': 'المنيب',

  // West Giza & New Cities
  'الهرم': 'الهرم',
  'شارع الهرم': 'الهرم',
  'نصر الدين': 'الهرم',
  'الطالبية': 'الهرم',
  'اللبيني': 'الهرم',
  'المريوطية': 'الهرم',
  'العمرانية': 'الهرم',
  'العمرانيه': 'الهرم',
  'ترسا': 'الهرم',
  'فيصل': 'فيصل',
  'شارع فيصل': 'فيصل',
  'العشرين': 'فيصل',
  'حدائق الاهرام': 'حدائق الأهرام',
  'حدائق-الاهرام': 'حدائق الأهرام',
  'حدائق-الأهرام': 'حدائق الأهرام',
  'حدائق الأهرام': 'حدائق الأهرام',
  'الرماية': 'حدائق الأهرام',
  '6 اكتوبر': '6 أكتوبر',
  '6-اكتوبر': '6 أكتوبر',
  '6-أكتوبر': '6 أكتوبر',
  '6 أكتوبر': '6 أكتوبر',
  '٦ اكتوبر': '6 أكتوبر',
  '٦-اكتوبر': '6 أكتوبر',
  'اكتوبر': '6 أكتوبر',
  'أكتوبر': '6 أكتوبر',
  'الحصري': '6 أكتوبر',
  'مول العرب': '6 أكتوبر',
  'الشيخ زايد': 'الشيخ زايد',
  'الشيخ-زايد': 'الشيخ زايد',
  'زايد': 'الشيخ زايد',
  'مدينة الشيخ زايد': 'الشيخ زايد',
  'مدينة-الشيخ-زايد': 'الشيخ زايد',
  'حدائق الشيخ زايد': 'الشيخ زايد',
  'كابيتال بيزنس بارك': 'الشيخ زايد',
  'أركان': 'الشيخ زايد',

  // North Cairo & Qalyubia
  'شبرا': 'شبرا',
  'شبرا مصر': 'شبرا',
  'شبرا البلد': 'شبرا',
  'روض الفرج': 'شبرا',
  'الساحل': 'شبرا',
  'موقف عبود': 'شبرا',
  'شبرا الخيمه': 'شبرا الخيمة',
  'شبرا-الخيمة': 'شبرا الخيمة',
  'شبرا-الخيمه': 'شبرا الخيمة',
  'شبرا الخيمة': 'شبرا الخيمة',
  'مسطرد': 'شبرا الخيمة',
  'بهتيم': 'شبرا الخيمة',
  'المطريه': 'المطرية',
  'المطرية': 'المطرية',

  // Highways & Axes
  'الدائري': 'الطريق الدائري',
  'الطريق الدائري': 'الطريق الدائري',
  'الطريق-الدائري': 'الطريق الدائري',
  'طريق الاوتوستراد': 'الأوتوستراد',
  'طريق-الاوتوستراد': 'الأوتوستراد',
  'طريق-الأوتوستراد': 'الأوتوستراد',
  'الاوتوستراد': 'الأوتوستراد',
  'الأوتوستراد': 'الأوتوستراد',
  'صلاح سالم': 'صلاح سالم',
  'صلاح-سالم': 'صلاح سالم',
  'طريق صلاح سالم': 'صلاح سالم',
  'طريق-صلاح-سالم': 'صلاح سالم',

  // New Cairo & Nasr City Corridor
  'التجمع الخامس مدينة نصر': 'التجمع الخامس مدينة نصر',
  'التجمع-الخامس-مدينة-نصر': 'التجمع الخامس مدينة نصر',
  'التجمع الخامس مدينه نصر': 'التجمع الخامس مدينة نصر',
  'التجمع-الخامس-مدينه-نصر': 'التجمع الخامس مدينة نصر',
  'نقل سيارات التجمع الخامس مدينة نصر': 'التجمع الخامس مدينة نصر',
  'ونش التجمع الخامس مدينة نصر': 'التجمع الخامس مدينة نصر',
  'ونش التجمع الخامس مدينه نصر': 'التجمع الخامس مدينة نصر',

  // Sayeda Aisha
  'السيدة عائشة': 'السيدة عائشة',
  'السيدة-عائشة': 'السيدة عائشة',
  'السيده عائشه': 'السيدة عائشة',
  'السيده-عائشه': 'السيدة عائشة',
  'ميدان السيدة عائشة': 'السيدة عائشة',
  'ميدان السيده عائشه': 'السيدة عائشة',
  'كوبري السيدة عائشة': 'السيدة عائشة',
  'كوبري السيده عائشه': 'السيدة عائشة',

  // El Tel El Kebir
  'التل الكبير': 'التل الكبير',
  'التل-الكبير': 'التل الكبير',
  'تل الكبير': 'التل الكبير',
  'تل-الكبير': 'التل الكبير',
  'مدينة التل الكبير': 'التل الكبير',
  'مركز التل الكبير': 'التل الكبير',

  // Abu Hammad
  'أبو حماد': 'أبو حماد',
  'أبو-حماد': 'أبو حماد',
  'ابو حماد': 'أبو حماد',
  'ابو-حماد': 'أبو حماد',
  'أبوحماد': 'أبو حماد',
  'ابوحماد': 'أبو حماد',
  'مركز أبو حماد': 'أبو حماد',
  'مركز ابو حماد': 'أبو حماد',
  'مدينة أبو حماد': 'أبو حماد',
  'مدينة ابو حماد': 'أبو حماد',
};

areas.forEach((canonicalName) => {
  const norm = normalizeArabicText(canonicalName);
  if (!slugMap.has(norm)) {
    slugMap.set(norm, canonicalName);
  }
  if (!slugMap.has(canonicalName)) {
    slugMap.set(canonicalName, canonicalName);
  }
  if (!slugMap.has(canonicalName.replace(/\s+/g, '-'))) {
    slugMap.set(canonicalName.replace(/\s+/g, '-'), canonicalName);
  }
});

Object.entries(manualAliases).forEach(([alias, target]) => {
  slugMap.set(alias, target);
  slugMap.set(normalizeArabicText(alias), target);
});

export function getCanonicalAreaName(inputSlug) {
  if (!inputSlug) return null;
  const clean = decodeURIComponent(String(inputSlug || '')).trim().replace(/\/+$/, '');
  
  if (slugMap.has(clean)) {
    return slugMap.get(clean);
  }
  
  const norm = normalizeArabicText(clean);
  if (slugMap.has(norm)) {
    return slugMap.get(norm);
  }
  
  return null;
}

export default slugMap;
